import { addYears, format, subYears } from 'date-fns';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { getDateInNY, parseISODate } from '@/utils/dateUtils';

interface TaskDatePickerProps {
  /**
   * Task date fields are stored as datetimes, but semantically treated as "date-only"
   * in the task timezone (America/New_York). Normalizing to an NY date avoids
   * cross-machine off-by-one day display issues.
   */
  value: string | null | undefined;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Called when the popover open state changes (e.g. to hide a parent tooltip) */
  onOpenChange?: (open: boolean) => void;
}

export const TaskDatePicker: React.FC<TaskDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Pick a date',
  className,
  disabled = false,
  onOpenChange,
}) => {
  const dateOnly = value ? getDateInNY(value) : null;
  const selectedDate = dateOnly ? parseISODate(dateOnly) : undefined;

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            'h-auto p-0 font-normal justify-start hover:bg-transparent hover:text-blue-600 text-left rounded-md',
            !dateOnly && 'text-slate-400',
            className,
          )}
        >
          {selectedDate ? format(selectedDate, 'MMM d, yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(newDate) => onChange(newDate || null)}
          captionLayout="dropdown"
          endMonth={addYears(new Date(), 5)}
          startMonth={subYears(new Date(), 5)}
        />
      </PopoverContent>
    </Popover>
  );
};
